import { type FC, useState, useEffect, useRef } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";

interface FolderNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "rename";
  initialName?: string;
  existingNames: string[];
  onSubmit: (name: string) => void;
  isLoading?: boolean;
}

export const FolderNameDialog: FC<FolderNameDialogProps> = ({
  open,
  onOpenChange,
  mode,
  initialName = "",
  existingNames,
  onSubmit,
  isLoading = false,
}) => {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setError("");
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (mode === "rename") {
            inputRef.current.select();
          }
        }
      });
    }
  }, [open, initialName, mode]);

  const validate = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const isDuplicate = existingNames.some(
      (n) => n.toLowerCase() === trimmed.toLowerCase() && n.toLowerCase() !== initialName.toLowerCase(),
    );
    return isDuplicate ? "You already have a folder with this name." : "";
  };

  const handleBlur = () => {
    setError(validate(name));
  };

  const handleSubmit = () => {
    const validationError = validate(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!name.trim()) return;
    onSubmit(name.trim());
  };

  const isDisabled = isLoading || !name.trim() || !!error;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New folder" : "Rename folder"}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <label className="text-sm text-secondary mb-1.5 block">Folder name</label>
          <Input
            ref={inputRef}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError("");
            }}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isDisabled) {
                handleSubmit();
              }
            }}
            className={error ? "border-destructive focus:border-destructive" : ""}
          />
          {error && <p className="text-sm text-destructive mt-2">{error}</p>}
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="outline" size="md" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="brand"
              size="md"
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {isLoading ? "Saving..." : mode === "create" ? "Create folder" : "Save"}
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
